import { SmaSignal } from "../constants";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/DetailsCommon/Forecast/utils/formatSignal";

/**
 * 技术指标信号格式化工具类
 * 用于将数值分数转换为可读的信号描述
 */
export type SentimentType = "Bullish" | "Bearish" | "Neutral";
/**
 * 根据分数获取情感标签（小写）
 * @param score 分数字符串
 * @returns 情感标签
 */
export const getTag = (score: string): SentimentType => {
  const numScore = Number(score);
  if (numScore > 0) {
    return "Bullish";
  }
  if (numScore < 0) {
    return "Bearish";
  }
  return "Neutral";
};

/**
 * 根据分数获取RSI信号描述
 * @param score 分数字符串
 * @returns RSI信号描述
 */
export const getRsiSignal = (score: string): string => {
  const numScore = Number(score);
  if (numScore > 0) {
    return "RSI<30 Oversold";
  }
  if (numScore < 0) {
    return "RSI>70 Overbought";
  }
  return "30<=RSI<=70";
};

export const getRsiSignalText = ({
  key,
  locale,
}: {
  key: string;
  locale?: string;
}) => {
  const I18n = I18nUtil.createScoped(Translations, locale);
  const rsiSignalMap = {
    "-1": I18n.t("overbought"),
    "0": I18n.t("neutral"),
    "1": I18n.t("oversold"),
  } as const;
  return rsiSignalMap[key as keyof typeof rsiSignalMap] || "";
};

/**
 * 根据分数获取MACD信号描述
 * @param score 分数字符串
 * @returns MACD信号描述
 */
export const getMacdSignal = (score: string): string => {
  const numScore = Number(score);
  if (numScore > 0) {
    return "MACD_12_26_9 > 0";
  }
  if (numScore < 0) {
    return "MACD_12_26_9 < 0";
  }
  return "MACD_12_26_9 = 0";
};

/**
 * 根据分数获取Stochastic信号描述
 * @param score 分数字符串
 * @returns Stochastic信号描述
 */
export const getStochasticSignal = (score: string): string => {
  const numScore = Number(score);
  if (numScore < 0) {
    return "Stochastic %K>80 Overbought";
  }
  if (numScore > 0) {
    return "Stochastic %K<20 Oversold";
  }
  return "20<=Stochastic %K<=80";
};

export const getSmaSignalValue = (
  smaStart?: number,
  smaEnd?: number,
): "0" | "1" | "-1" => {
  if (!smaStart || !smaEnd) {
    return "0";
  }
  if (smaStart > smaEnd) {
    return "1";
  } else if (smaStart < smaEnd) {
    return "-1";
  }
  return "0";
};

export const getSmaSignal = (
  signal: SmaSignal,
  score: "0" | "1" | "-1",
  baseIndicator: string,
): string | undefined => {
  // SMA 5 Below SMA10 SMA5 < SMA10  bearish -1
  if (signal === SmaSignal.SMA5_TO_SMA10) {
    if (score === "1") {
      return `${baseIndicator} 5 Above ${baseIndicator} 10`;
    } else if (score === "-1") {
      return `${baseIndicator} 5 Below ${baseIndicator} 10`;
    }
    return `${baseIndicator} 5 = ${baseIndicator} 10`;
  }
  if (signal === SmaSignal.SMA10_TO_SMA20) {
    if (score === "1") {
      return `${baseIndicator} 10 Above ${baseIndicator} 20`;
    }
    if (score === "-1") {
      return `${baseIndicator} 10 Below ${baseIndicator} 20`;
    }
    return `${baseIndicator} 10 = ${baseIndicator} 20`;
  }

  if (signal === SmaSignal.SMA20_TO_SMA30) {
    if (score === "1") {
      return `${baseIndicator} 20 Above ${baseIndicator} 30`;
    }
    if (score === "-1") {
      return `${baseIndicator} 20 Below ${baseIndicator} 30`;
    }
    return `${baseIndicator} 20 = ${baseIndicator} 30`;
  }

  if (signal === SmaSignal.SMA30_TO_SMA60) {
    if (score === "1") {
      return `${baseIndicator} 30 Above ${baseIndicator} 60`;
    }
    if (score === "-1") {
      return `${baseIndicator} 30 Below ${baseIndicator} 60`;
    }
    return `${baseIndicator} 30 = ${baseIndicator} 60`;
  }
};
